import {
    useImperativeHandle,
    useLayoutEffect,
    forwardRef,
    useEffect,
    useState,
    useMemo,
    useRef
} from "react";
import {
    AccessibilityInfo,
    Animated,
    Platform,
    View
} from "react-native";
import type {
    IYearSelectorRef,
    CalendarYear
} from "./type";
import type IYearSelectorProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    YEAR_SELECTOR_NAVIGATION_KEYS,
    getYearSelectorNavigationView,
    getYearSelectorNavigationDate,
    YEAR_SELECTOR_LABEL_SEPARATOR,
    YEAR_SELECTOR_TITLE_SEPARATOR,
    getYearSelectorSelectedDate,
    getYearSelectorTabStopIndex,
    getYearSelectorRangeResult,
    YEAR_SELECTOR_COLUMN_COUNT,
    getYearSelectorNavigation,
    getYearSelectorPageIndex,
    YEAR_SELECTOR_YEAR_COUNT,
    getYearSelectorPeriod,
    getYearSelectorYears,
    getYearSelectorRows,
    isValidDate,
    dateSelect
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitToast
} from "../../core/hooks";
import {
    type RefForwardingComponent,
    type NCoreUIKitIcon
} from "../../types";
import {
    useBreakpoint
} from "../../helpers/pointer";
import {
    useDateSelectorSwap
} from "../dateSelector/hook";
import type {
    CalendarCellKeyboardEventType
} from "../calendarCell/type";
import {
    ChevronRight as ChevronRightIcon,
    ChevronLeft as ChevronLeftIcon
} from "lucide-react-native";
import moment from "moment";
import useCalendarCellHandlers from "../calendarCell/hook";
import AnimatedIcon from "../animatedIcon";
import CalendarCell from "../calendarCell";
import Button from "../button";
import Text from "../text";

const PreviousPeriodIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    icon={ChevronLeftIcon}
    animation="signature"
    color={color}
    size={size}
/>;

const NextPeriodIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    icon={ChevronRightIcon}
    animation="signature"
    color={color}
    size={size}
/>;

const YearSelector: RefForwardingComponent<IYearSelectorRef, IYearSelectorProps> = ({
    multipleSelectMinimumRequiredDayCount,
    isShowTodayIndicator = true,
    multipleSelectDayLimit,
    setIsSheetContentReady,
    viewDate: viewDateProp,
    selectMultipleObject,
    isShowCard = true,
    customLocalize,
    selectObject,
    customTheme,
    dateRange,
    maxDate,
    minDate,
    variant,
    date
}, ref) => {
    const {
        controlHeights,
        shapeConfigs,
        radiuses,
        borders,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const cellRefs = useRef<Array<View | null>>([]);

    const [
        viewDate,
        setViewDate
    ] = useState(() => isValidDate(viewDateProp) ? viewDateProp : dateSelect({
        dateRange,
        variant,
        date
    }));

    const [
        activeDate,
        setActiveDate
    ] = useState<Date | undefined>(undefined);

    const [
        focusRequest,
        setFocusRequest
    ] = useState(0);

    const years = useMemo(() => getYearSelectorYears({
        dateRange,
        viewDate,
        maxDate,
        minDate,
        variant,
        date
    }), [
        dateRange,
        viewDate,
        maxDate,
        minDate,
        variant,
        date
    ]);

    const isWeb = Platform.OS === "web";

    const tabStopIndex = getYearSelectorTabStopIndex({
        activeDate,
        years
    });

    const title = getYearSelectorPeriod({
        separator: YEAR_SELECTOR_TITLE_SEPARATOR,
        activeLocale,
        viewDate
    });

    const announcedTitleRef = useRef(title);

    const currentNavigation = getYearSelectorNavigation({
        viewDate,
        maxDate,
        minDate
    });

    const yearsLabel = localize("year-selector-group-label", [
        getYearSelectorPeriod({
            separator: YEAR_SELECTOR_LABEL_SEPARATOR,
            activeLocale,
            viewDate
        })
    ]);

    const groupLabel = variant === "range" ? localize("calendar-range-group-label", [
        yearsLabel
    ]) : yearsLabel;

    const breakpoint = useBreakpoint();

    const {
        nextPrevToolContainer: nextPrevToolContainerDynamicStyle,
        contentContainer: contentContainerDynamicStyle,
        container: containerDynamicStyle
    } = useStyles({
        isCardShown: isShowCard && breakpoint !== "compact",
        isStretched: !isShowCard,
        controlHeights,
        shapeConfigs,
        radiuses,
        borders,
        spaces,
        colors
    });

    const swapStyle = useDateSelectorSwap({
        pageIndex: getYearSelectorPageIndex({
            viewDate
        }),
        customTheme
    });

    useLayoutEffect(() => {
        setIsSheetContentReady?.(true);
    }, [
        years
    ]);

    useLayoutEffect(() => {
        if(!focusRequest || !activeDate) {
            return;
        }

        const activeIndex = years.findIndex((yearItem) => yearItem.date.isSame(activeDate, "year"));

        cellRefs.current[activeIndex]?.focus();
    }, [
        focusRequest
    ]);

    useEffect(() => {
        if(isValidDate(viewDateProp)) {
            setViewDate(viewDateProp);
        }
    }, [
        viewDateProp
    ]);

    useEffect(() => {
        if(announcedTitleRef.current === title) {
            return;
        }

        announcedTitleRef.current = title;

        if(Platform.OS !== "ios") {
            return;
        }

        AccessibilityInfo.announceForAccessibility(title);
    }, [
        title
    ]);

    useImperativeHandle(
        ref,
        () => ({
            changeCurrentView: (date: Date) => {
                if(!isValidDate(date)) {
                    return;
                }

                setViewDate(date);
            }
        }),
        []
    );

    const onPreviousPeriodPress = () => {
        setViewDate((currentViewDate) => moment(currentViewDate).subtract(YEAR_SELECTOR_YEAR_COUNT, "years").toDate());
    };

    const onNextPeriodPress = () => {
        setViewDate((currentViewDate) => moment(currentViewDate).add(YEAR_SELECTOR_YEAR_COUNT, "years").toDate());
    };

    const onYearPress = (yearItem: CalendarYear) => {
        if(variant === "single") {
            selectObject(getYearSelectorSelectedDate({
                year: yearItem.date,
                date
            }));
            return;
        }

        if(variant !== "range") {
            return;
        }

        const rangeResult = getYearSelectorRangeResult({
            multipleSelectMinimumRequiredDayCount,
            multipleSelectDayLimit,
            year: yearItem.date,
            dateRange
        });

        if(rangeResult.errorKey) {
            NCoreUIKitToast.open({
                title: localize(rangeResult.errorKey)
            });
            return;
        }

        selectMultipleObject(rangeResult.dateRange);
    };

    const handleActiveDateChange = (nextDate: Date) => {
        setActiveDate((currentDate) => currentDate && moment(currentDate).isSame(nextDate, "year") ? currentDate : nextDate);
    };

    const onYearKeyDown = (event: CalendarCellKeyboardEventType, yearItem: CalendarYear) => {
        if(event.altKey || event.ctrlKey || event.metaKey || !YEAR_SELECTOR_NAVIGATION_KEYS.includes(event.key)) {
            return;
        }

        event.preventDefault();

        const targetYear = getYearSelectorNavigationDate({
            year: yearItem.date,
            key: event.key,
            direction,
            viewDate,
            maxDate,
            minDate
        });

        if(!targetYear) {
            return;
        }

        setFocusRequest((currentRequest) => currentRequest + 1);

        handleActiveDateChange(targetYear.toDate());

        const nextViewDate = getYearSelectorNavigationView({
            target: targetYear,
            viewDate
        });

        if(nextViewDate) {
            setViewDate(nextViewDate);
        }
    };

    const getYearTabIndex = (yearIndex: number): 0 | -1 => {
        return yearIndex === tabStopIndex ? 0 : -1;
    };

    const getCellHandlers = useCalendarCellHandlers({
        onFocus: (yearIndex) => {
            const yearItem = years[yearIndex];

            if(yearItem) {
                handleActiveDateChange(yearItem.date.toDate());
            }
        },
        onKeyDown: (event, yearIndex) => {
            const yearItem = years[yearIndex];

            if(yearItem) {
                onYearKeyDown(event, yearItem);
            }
        },
        onPress: (yearIndex) => {
            const yearItem = years[yearIndex];

            if(yearItem) {
                onYearPress(yearItem);
            }
        },
        cellRefs
    });

    const renderYear = (yearItem: CalendarYear, yearIndex: number) => {
        const cellHandlers = getCellHandlers(yearIndex);

        return <CalendarCell
            tabIndex={isWeb ? getYearTabIndex(yearIndex) : undefined}
            onKeyDown={isWeb ? cellHandlers.onKeyDown : undefined}
            onFocus={isWeb ? cellHandlers.onFocus : undefined}
            isToday={isShowTodayIndicator && yearItem.isToday}
            rangePosition={yearItem.rangePosition}
            isDisabled={yearItem.isDisabled}
            isSelected={yearItem.isSelected}
            customLocalize={customLocalize}
            onPress={cellHandlers.onPress}
            date={yearItem.date.toDate()}
            key={`year-${yearIndex}`}
            customTheme={customTheme}
            spreadBehaviour="stretch"
            ref={cellHandlers.ref}
            calendarType="year"
        />;
    };

    const renderNextPrevTool = () => {
        return <View
            style={[
                stylesheet.nextPrevToolContainer,
                nextPrevToolContainerDynamicStyle
            ]}
        >
            <Button
                accessibilityLabel={localize("previous-x-years", [
                    YEAR_SELECTOR_YEAR_COUNT
                ])}
                isDisabled={currentNavigation.isPreviousDisabled}
                onPress={onPreviousPeriodPress}
                customTheme={customTheme}
                icon={PreviousPeriodIcon}
                spreadBehaviour="free"
                variant="ghost"
                type="neutral"
                size="medium"
            />
            <Text
                customTheme={customTheme}
                variant="titleMediumSize"
                style={stylesheet.title}
                aria-live="polite"
                numberOfLines={1}
                color="high"
            >
                {title}
            </Text>
            <Button
                accessibilityLabel={localize("next-x-years", [
                    YEAR_SELECTOR_YEAR_COUNT
                ])}
                isDisabled={currentNavigation.isNextDisabled}
                onPress={onNextPeriodPress}
                customTheme={customTheme}
                spreadBehaviour="free"
                icon={NextPeriodIcon}
                variant="ghost"
                type="neutral"
                size="medium"
            />
        </View>;
    };

    const renderYears = () => {
        return getYearSelectorRows(years).map((rowYears, rowIndex) => {
            return <View
                key={`year-row-${rowIndex}`}
                style={[
                    stylesheet.rowContainer
                ]}
            >
                {rowYears.map((yearItem, yearIndex) => renderYear(yearItem, rowIndex * YEAR_SELECTOR_COLUMN_COUNT + yearIndex))}
            </View>;
        });
    };

    return <View
        style={[
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        {renderNextPrevTool()}
        <Animated.View
            style={[
                stylesheet.contentContainer,
                contentContainerDynamicStyle,
                swapStyle
            ]}
            aria-label={groupLabel}
            role="group"
        >
            {renderYears()}
        </Animated.View>
    </View>;
};
export default forwardRef(YearSelector);
